/**
 * Launcher icon set + the device-function list, shared by the photo-forward
 * launcher templates (Aurelia / Marea / Nord / Terra / Tann).
 *
 * Ported from the "Hotel TV Templates" Claude Design handoff (shared.jsx). The
 * function set is the real Foyer launcher - TV · Screen Share · Apps ·
 * Information · Radio - with TV always the first, primary action.
 */

/** A function in the dock (TV is the launch action; the rest open sub-pages). */
export type FunctionKey =
  | "tv"
  | "cast"
  | "apps"
  | "info"
  | "radio"
  | "wifi"
  | "welcome"
  | "relatives"
  | "instagram"
  | "weather"
  | "custom"
  | "offers"
  // Foyer's OWN player (TV-3a). Distinct from `tv`, which is the launch action
  // that hands the screen to another application entirely.
  | "vod"
  | "guide";

/** Every screen the launcher can show. "language"/"input" open from the
 *  header icons (globe / settings); the rest from the function dock. */
export type LauncherScreen = "home" | FunctionKey | "language" | "input";

export type IconName = FunctionKey | "settings" | "globe" | "cloud";

export interface FunctionDef {
  key: FunctionKey;
  label: string;
  icon: IconName;
}

/** TV is always first and always present (hotel-TV's primary function). */
export const LAUNCHER_FUNCTIONS: FunctionDef[] = [
  { key: "tv", label: "TV", icon: "tv" },
  { key: "cast", label: "Screen Share", icon: "cast" },
  { key: "apps", label: "Apps", icon: "apps" },
  { key: "info", label: "Information", icon: "info" },
  { key: "radio", label: "Radio", icon: "radio" },
  { key: "wifi", label: "Wi-Fi", icon: "wifi" },
  { key: "welcome", label: "Welcome", icon: "welcome" },
  // Relatives (Anhöriga) is a care-only screen - see CareScheme. Not offered on
  // the hotel launchers, so it's intentionally absent from this list.
  { key: "instagram", label: "Instagram", icon: "instagram" },
  { key: "weather", label: "Weather", icon: "weather" },
  // The premise's own page. "Custom" is the fallback only - `visibleFunctions`
  // rewrites this label from the `customTitle` knob, because the whole point of
  // the screen is that the property names it.
  { key: "custom", label: "Custom", icon: "custom" },
  // What the hotel sells through the television (POS-3). Like `custom`, the
  // label here is only a fallback - `visibleFunctions` rewrites it from the
  // `offersTitle` knob, because a property calls this "Erbjudanden" or "In-room
  // dining" and neither is a template decision.
  { key: "offers", label: "Offers", icon: "offers" },
  // Foyer's own streaming client (TV-3a). Labels are fallbacks again -
  // `visibleFunctions` rewrites both from `vodTitle` / `guideTitle`.
  { key: "vod", label: "Films & series", icon: "vod" },
  { key: "guide", label: "TV guide", icon: "guide" },
];

interface IconProps {
  name: IconName;
  size?: number;
  color?: string;
  stroke?: number;
}

/** Simple geometric line-glyph set matching the design's `Icon`. */
export function LauncherIcon({
  name,
  size = 28,
  color = "currentColor",
  stroke = 1.7,
}: IconProps) {
  const p = {
    fill: "none",
    stroke: color,
    strokeWidth: stroke,
    strokeLinecap: "round" as const,
    strokeLinejoin: "round" as const,
  };
  const dot = (cx: number, cy: number, r = 0.7) => (
    <circle cx={cx} cy={cy} r={r} fill={color} stroke="none" />
  );
  let body: React.ReactNode;
  switch (name) {
    case "tv":
      body = (
        <>
          <rect x="2.5" y="4.5" width="19" height="13" rx="2" {...p} />
          <path d="M8.5 21h7M12 17.5V21" {...p} />
        </>
      );
      break;
    case "cast":
      body = (
        <>
          <rect x="3" y="3" width="18" height="12" rx="2" {...p} />
          <path d="M2.5 17a4.5 4.5 0 0 1 4.5 4.5" {...p} />
          <path d="M2.5 19.6a2 2 0 0 1 2 2" {...p} />
          {dot(2.7, 21.3, 0.6)}
        </>
      );
      break;
    case "apps": {
      const r: React.ReactNode[] = [];
      for (let y = 0; y < 3; y++)
        for (let x = 0; x < 3; x++)
          r.push(
            <rect
              key={`${x}${y}`}
              x={4 + x * 6.4}
              y={4 + y * 6.4}
              width="4"
              height="4"
              rx="1"
              {...p}
            />
          );
      body = <>{r}</>;
      break;
    }
    case "info":
      body = (
        <>
          <circle cx="12" cy="12" r="9" {...p} />
          <path d="M12 11v5.2" {...p} />
          {dot(12, 7.8, 0.75)}
        </>
      );
      break;
    case "radio":
      body = (
        <>
          <path d="M6.5 9 18 4.2" {...p} />
          <rect x="2.5" y="9" width="19" height="11.5" rx="2" {...p} />
          <circle cx="8" cy="14.8" r="2.7" {...p} />
          <path d="M15 13.2h4M15 16.6h4" {...p} />
        </>
      );
      break;
    case "wifi":
      body = (
        <>
          <path d="M4.5 9.5a11 11 0 0 1 15 0" {...p} />
          <path d="M7.4 12.6a7 7 0 0 1 9.2 0" {...p} />
          <path d="M10 15.6a3 3 0 0 1 4 0" {...p} />
          {dot(12, 18.6, 0.85)}
        </>
      );
      break;
    case "welcome":
      body = (
        <>
          <rect x="3" y="5.5" width="18" height="13" rx="2" {...p} />
          <path d="M3.6 7 12 13l8.4-6" {...p} />
        </>
      );
      break;
    case "relatives":
      body = (
        <>
          <circle cx="8.5" cy="8.5" r="2.6" {...p} />
          <circle cx="16" cy="9.5" r="2.1" {...p} />
          <path d="M3.5 19c0-2.8 2.2-4.6 5-4.6s5 1.8 5 4.6" {...p} />
          <path d="M14.5 14.7c2.2.1 4 1.7 4 4.3" {...p} />
        </>
      );
      break;
    case "instagram":
      body = (
        <>
          <rect x="3.5" y="3.5" width="17" height="17" rx="4.5" {...p} />
          <circle cx="12" cy="12" r="4" {...p} />
          {dot(17, 7, 0.9)}
        </>
      );
      break;
    case "weather":
      body = (
        <>
          <circle cx="9" cy="9.5" r="3.6" {...p} />
          <path
            d="M9 2.2v1.8M9 15v1.8M2.2 9.5H4M14 9.5h1.8M4.2 4.7l1.2 1.2M12.6 13.1l1.2 1.2M13.8 4.7l-1.2 1.2"
            {...p}
          />
          <path
            d="M11 19a3.4 3.4 0 0 1 .4-6.8 4.1 4.1 0 0 1 7.8-.8 2.9 2.9 0 0 1 .4 7.6H11z"
            {...p}
          />
        </>
      );
      break;
    case "custom":
      body = (
        <>
          <rect x="2.6" y="4.4" width="18.8" height="15.2" rx="2.2" {...p} />
          <path d="M2.6 15.4l4.6-4.2 4 3.4 3.4-3 6.8 5.6" {...p} />
          {dot(8.4, 9, 1.1)}
        </>
      );
      break;
    // A reception bell: the one glyph that reads as "ask the hotel for
    // something" at three metres, and the only image all three offer kinds
    // (order, booking, promotion) share honestly. A shopping trolley would
    // promise a checkout the television does not have.
    case "offers":
      body = (
        <>
          <path d="M4 16.4a8 8 0 0 1 16 0" {...p} />
          <path d="M2.4 16.4h19.2" {...p} />
          <path d="M6.6 19.4h10.8" {...p} />
          <path d="M12 8.4V6.9" {...p} />
          {dot(12, 5.6, 1.1)}
        </>
      );
      break;
    // A film strip stood on end: the one glyph that reads as "something to
    // watch, on demand" at three metres without borrowing the `tv` rectangle,
    // which already means "hand the screen to the tuner".
    case "vod":
      body = (
        <>
          <rect x="4.4" y="3" width="15.2" height="18" rx="2" {...p} />
          <path d="M8.2 3v18M15.8 3v18" {...p} />
          <path d="M4.4 9h3.8M15.8 9h3.8M4.4 15h3.8M15.8 15h3.8" {...p} />
        </>
      );
      break;
    // A grid of programme cells with a "now" line down it - the shape of the
    // guide itself, which is what a guest is looking for on the dock.
    case "guide":
      body = (
        <>
          <rect x="2.6" y="4.6" width="18.8" height="14.8" rx="2" {...p} />
          <path d="M2.6 9.4h18.8" {...p} />
          <path d="M9.6 9.4v10M15.4 9.4v10" {...p} />
          <path d="M6.4 2.6v3.4M17.6 2.6v3.4" {...p} />
        </>
      );
      break;
    case "settings": {
      const teeth: React.ReactNode[] = [];
      for (let i = 0; i < 8; i++) {
        const a = (i * Math.PI) / 4;
        teeth.push(
          <path
            key={i}
            d={`M${12 + Math.cos(a) * 6} ${12 + Math.sin(a) * 6}L${
              12 + Math.cos(a) * 8.2
            } ${12 + Math.sin(a) * 8.2}`}
            {...p}
          />
        );
      }
      body = (
        <>
          <circle cx="12" cy="12" r="4.4" {...p} />
          {teeth}
        </>
      );
      break;
    }
    case "globe":
      body = (
        <>
          <circle cx="12" cy="12" r="9" {...p} />
          <path d="M3 12h18" {...p} />
          <path
            d="M12 3c3.2 2.6 3.2 15.4 0 18M12 3c-3.2 2.6-3.2 15.4 0 18"
            {...p}
          />
        </>
      );
      break;
    case "cloud":
      body = (
        <path
          d="M7 17.4a4.3 4.3 0 0 1 .5-8.5 5.2 5.2 0 0 1 9.9-1 3.6 3.6 0 0 1 .5 9.5H7z"
          {...p}
        />
      );
      break;
    default:
      body = <circle cx="12" cy="12" r="8" {...p} />;
  }
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      aria-hidden="true"
      style={{ display: "block" }}
    >
      {body}
    </svg>
  );
}
